import React, { useState, useEffect } from 'react';
import { Milestone } from '../types';
import { getMilestonesByGoal } from '../firebase/milestones.service';
import MilestoneCard from './MilestoneCard';

interface MilestoneOverviewProps {
  goalId: string;
  ownerId: string;
}

/**
 * MilestoneOverview - Overzicht van milestones voor een goal
 * 
 * Beantwoordt de vraag:
 * "Welke herkenningspunten heb ik voor dit doel gedefinieerd, 
 *  en wat betekenen ze voor mij?"
 * 
 * Toont:
 * - De sparring vraag die leidde tot de milestone (indien beschikbaar)
 * - Het antwoord/de betekenis van de gebruiker (bewerkbaar)
 */
const MilestoneOverview: React.FC<MilestoneOverviewProps> = ({
  goalId,
  ownerId,
}) => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadMilestones = async () => {
      setIsLoading(true);
      try {
        const data = await getMilestonesByGoal(ownerId, goalId);
        // Sort by createdAt ASC (oldest first)
        const sorted = [...data].sort((a, b) => a.createdAt - b.createdAt);
        setMilestones(sorted);
      } catch (err) {
        console.error('Failed to load milestones:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadMilestones();
  }, [goalId, ownerId]);

  // Handle milestone update from card
  const handleMilestoneUpdate = (updatedMilestone: Milestone) => {
    setMilestones(prev => 
      prev.map(m => m.id === updatedMilestone.id ? updatedMilestone : m)
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Laden...
      </div>
    );
  }

  if (milestones.length === 0) {
    return null; // Niet tonen als er geen milestones zijn
  }

  return (
    <div className="space-y-4">
      {/* Header - subtle, not attention-grabbing */}
      <div className="flex items-center gap-2">
        <h3 className="text-xs text-gray-400 tracking-wide uppercase">
          Herkenningspunten
        </h3>
        <span className="text-xs text-gray-300">
          {milestones.length}
        </span>
      </div>

      {/* Cards with gentle spacing */}
      <div className="space-y-3">
        {milestones.map((milestone) => (
          <MilestoneCard 
            key={milestone.id} 
            milestone={milestone} 
            onUpdate={handleMilestoneUpdate}
          />
        ))}
      </div>
    </div>
  );
};

export default MilestoneOverview;
